import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { IBenefit } from "../../interface/motor/motor-quotation.interface";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import { ProductDocumentInterface } from "src/app/comparison/interface/motor-quotation.interface";
import { OrderDataService } from "src/app/comparison/data-service/order-data.service";

@Component({
    selector: "app-insurer-card",
    templateUrl: "./insurer-card.component.html",
    styleUrls: ["./insurer-card.component.scss"],
    standalone: false
})
export class InsurerCardComponent implements OnInit {
	benefits: IBenefit[] = [];
	totalBenefits: number;
	showMoreBenefits = true;
	documents: ProductDocumentInterface[] = [];
	isVehicleTypeAccess: string;

	@Input() insuranceType: string;
	@Input() first: boolean = false;
	@Input() item: any;
	@Input() showTag: boolean;
	@Input() requestData: any;
	@Output() onSubmit = new EventEmitter();

	constructor(
		public translate: TranslateService,
		private orderDataService: OrderDataService
	) {
		this.translate.onLangChange.subscribe((_event: LangChangeEvent) =>
			this.getProductDocument()
		);
	}

	ngOnInit(): void {
		this.insuranceType =
			this.item.requestType == "MotorcycleTPRequest"
				? "TP"
				: this.insuranceType;
		this.isVehicleTypeAccess = this.requestData.vehicleType;
		this.getProductDocument();
		this.displayBenefits();
	}

	get totalBasePremiumAfterRebate() {
		return this.item?.totalBasePremium - this.item?.rebate || 0;
	}

	getRibbonType(partnerName: string): string {
		let ribbonType: string;
		const storageUrl: string =
			"https://storage.googleapis.com/dev-ps-asset/affiliate/logo/";

		const { dealerType, eHailingUsed } = this.requestData;

		const ehailingMap = {
			POLICYSTREET: {
				true: {
					daily_annual: ["Allianz", "AXA"],
					daily: ["TMIM", "Zurich_Takaful"]
				},
				false: {
					daily_annual: [],
					daily: ["Zurich_Takaful"]
				}
			},
			CARSOMEDR: {
				true: {
					daily_annual: ["Allianz", "AXA"],
					daily: ["Zurich_Takaful"]
				},
				false: {
					daily_annual: ["AXA"],
					daily: ["TMIM", "Zurich_Takaful"]
				}
			}
		};

		const config = ehailingMap[dealerType]?.[eHailingUsed];

		if (config?.daily_annual.includes(partnerName)) ribbonType = "daily_annual";
		if (config?.daily.includes(partnerName)) ribbonType = "daily";

		if ([...config?.daily_annual, ...config?.daily].includes(partnerName)) {
			const dealer = dealerType === "POLICYSTREET" ? "psdr" : "carsome";

			return `${storageUrl}${dealer}_${ribbonType}_${this.translate.currentLang}.png`;
		} else {
			return "assets/image/placeholder/empty_en.png"; // Fallback
		}
	}
	get totalPrice() {
		return this.totalCharges + 1;
	}

	get totalBasePremium() {
		return this.item?.totalBasePremium;
	}

	get basePremium() {
		return this.item?.basePremium;
	}

	get totalCharges() {
		return (
			this.totalBasePremium +
			(this.item?.taxRate * this.totalBasePremium + this.item?.stampDutyPrice)
		);
	}

	get getSILabelName() {
		return this.item?.isTakaful ? "common.sumCovered" : "common.sumInsured";
	}

	get getPolicyLabelName() {
		return this.item?.isTakaful
			? "insurerDetails.subjectToCertificate"
			: "insurerDetails.subjectToPolicy";
	}

	get eppEligibility() {
		let eppEligibilityObject: { isEligible: boolean; months: number } = null;
		const eppAmount = this.totalBasePremiumAfterRebate;

		if (eppAmount >= 500 && eppAmount < 999) {
			eppEligibilityObject = { isEligible: true, months: 6 };
		}

		if (eppAmount > 999) {
			eppEligibilityObject = { isEligible: true, months: 12 };
		}

		return eppEligibilityObject;
	}

	hasTranslation(key: string): boolean {
		const translation = this.translate.instant(key);
		if (translation.length) return true;
		return false;
	}

	toggleMoreBenefits() {
		if (this.showMoreBenefits) {
			this.showMoreBenefits = false;
		} else {
			this.showMoreBenefits = true;
		}
		this.displayBenefits();
	}

	handleBuyNow() {
		this.onSubmit.emit(this.item.id);
	}

	private getProductDocument() {
		if (!this.item) return;
		this.orderDataService
			.getProductDocumentById(
				this.item.productId,
				this.translate.currentLang,
				this.insuranceType
			)
			.subscribe({
				next: (x) => {
					this.documents = x;
				}
			});
	}

	private displayBenefits() {
		let benefitList;

		if (this.isVehicleTypeAccess == "Motorcycle") {
			benefitList =
				this.insuranceType === "TP"
					? MOTORCYCLE_INSURER_BENEFITS_LIST_TP
					: MOTORCYCLE_INSURER_BENEFITS_LIST;
		} else {
			benefitList =
				this.insuranceType === "TPFT"
					? INSURER_BENEFITS_LIST_TPFT
					: INSURER_BENEFITS_LIST;
		}

		let tempBenefits: { type: string; description: string }[] = benefitList[
			this.item.code
		].filter((x) => this.hasTranslation(x.description));

		//totalBenefits is used to show user the total number of benefits there are
		// before user clicks on "See all benefits"
		this.totalBenefits = tempBenefits.length;

		if (this.showMoreBenefits) {
			this.benefits = tempBenefits;
		} else {
			this.benefits = tempBenefits.slice(0, 4);
		}
	}
}

export const MOTORCYCLE_INSURER_BENEFITS_LIST = {
	TMIM: [
		{ type: "allRiders", description: "benefit.TMIM.allRiders" },
		{
			type: "24hrTowing",
			description: "benefit.TMIM.motorcycleEmergencyTowing24Hrs"
		},
		{ type: "free", description: "benefit.TMIM.roadsideAssistance" },
		{ type: "", description: "benefit.TMIM.motorcycleBodyInjury" }
	],
	AXA: [
		{ type: "allRiders", description: "benefit.AXA.allRiders" },
		{
			type: "24hrTowing",
			description: "benefit.AXA.motorcycleEmergencyTowing24Hrs"
		},
		{ type: "free", description: "benefit.AXA.roadsideAssistance" },
		{ type: "free", description: "benefit.AXA.roadsideRepair" },
		{ type: "", description: "benefit.AXA.instantApproval" },
		{ type: "", description: "benefit.AXA.motorcycleBodyInjury" },
		{ type: "", description: "benefit.AXA.coverForFEL" },
		{ type: "", description: "benefit.AXA.coverForTheft" }
	],
	Zurich_Takaful: [
		{ type: "allRiders", description: "benefit.Zurich_Takaful.allRiders" },
		{
			type: "24hrTowing",
			description: "benefit.Zurich_Takaful.motorcycleEmergencyTowing24Hrs"
		},
		{ type: "free", description: "benefit.Zurich_Takaful.roadsideAssistance" },
		{ type: "", description: "benefit.Zurich_Takaful.motorcycleBodyInjury" }
	],
	Allianz: [
		{ type: "allRiders", description: "benefit.Allianz.allRiders" },
		{
			type: "24hrTowing",
			description: "benefit.Allianz.motorcycleEmergencyTowing24Hrs"
		},
		{ type: "free", description: "benefit.Allianz.motorcycleBatteryService" },
		{ type: "free", description: "benefit.Allianz.motorcycleFuelTankService" },
		{ type: "free", description: "benefit.Allianz.motorcycleTyreService" },
		{
			type: "free",
			description: "benefit.Allianz.motorcycleRoadsideAssistance"
		},
		{ type: "free", description: "benefit.Allianz.motorcycleFloodRelief" },
		{
			type: "free",
			description: "benefit.Allianz.motorcycleRiderPillionProtection"
		},
		{ type: "", description: "benefit.Allianz.motorcycleBodyInjury" },
		{ type: "", description: "benefit.Allianz.motorcycleFEL" },
		{ type: "", description: "benefit.Allianz.motorcycleCoverForTheft" }
	]
};

export const MOTORCYCLE_INSURER_BENEFITS_LIST_TP = {
	Zurich_Takaful: [
		{ type: "allRiders", description: "benefit.Zurich_Takaful.allRiders" },
		{
			type: "24hrTowing",
			description: "benefit.Zurich_Takaful.motorcycleEmergencyTowing24Hrs"
		},
		{ type: "free", description: "benefit.Zurich_Takaful.roadsideAssistance" },
		{ type: "", description: "benefit.Zurich_Takaful.motorcycleBodyInjury" },
		{ type: "", description: "benefit.AXA.coverForFEL" }
	],
	AXA: [
		{ type: "allRiders", description: "benefit.AXA.allRiders" },
		{ type: "", description: "benefit.AXA.motorcycleBodyInjury" }
	]
};

export const INSURER_BENEFITS_LIST = {
	TMIM: [
		{ type: "driver", description: "benefit.TMIM.additionalProtectedDriver" },
		{ type: "24hrTowing", description: "benefit.TMIM.benefitTowingService" },
		{
			type: "24hrTowing",
			description: "benefit.TMIM.benefitsTowingServiceAccident"
		},
		{ type: "free", description: "benefit.TMIM.changeBatteryService" },
		{ type: "free", description: "benefit.TMIM.tireReplacement" },
		{ type: "free", description: "benefit.TMIM.roadsideAssistance" },
		{ type: "", description: "benefit.TMIM.instantApproval" }
	],
	GrabAXA: [
		{
			type: "driver",
			description: "benefit.AXAGrab.additionalProtectedDriver"
		},
		{ type: "towing", description: "benefit.AXAGrab.benefitTowingService" },
		{ type: "free", description: "benefit.AXAGrab.warranty" },
		{ type: "free", description: "benefit.AXAGrab.roadsideAssistance" },
		{ type: "free", description: "benefit.AXAGrab.rideupTo30KM" },
		{ type: "free", description: "benefit.AXAGrab.axaPreferredWorkshop" },
		{ type: "", description: "benefit.AXAGrab.safeDrivingDiscountWithDesc" },
		{ type: "", description: "benefit.AXAGrab.instantApproval" },
		{ type: "", description: "benefit.AXAGrab.fastClaimApproval" },
		{ type: "", description: "benefit.AXAGrab.personalAccidentCarOwner" },
		{ type: "", description: "benefit.AXAGrab.medicalClaims" },
		{ type: "", description: "benefit.AXAGrab.lossOfIncome" },
		{ type: "", description: "benefit.AXAGrab.loanProtection" },
		{ type: "", description: "benefit.AXAGrab.wheelAlignment" }
	],
	MSIG: [
		{ type: "driver", description: "benefit.MSIG.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.MSIG.benefitTowingService" },
		{ type: "free", description: "benefit.MSIG.emergencyTowing" },
		{ type: "free", description: "benefit.MSIG.additionalDriver" },
		{ type: "free", description: "benefit.MSIG.roadsideAssistance" },
		{ type: "free", description: "benefit.MSIG.claimAssistance" },
		{ type: "", description: "benefit.MSIG.ehailing" }
	],
	Pando: [
		{ type: "driver", description: "benefit.pando.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.pando.benefitTowingService" },
		{ type: "free", description: "benefit.pando.towingService" },
		{ type: "free", description: "benefit.pando.roadsideAssistance" },
		{ type: "free", description: "benefit.pando.changeBatteryService" },
		{ type: "free", description: "benefit.pando.refuelEmptyTank" },
		{ type: "free", description: "benefit.pando.tireReplacement" },
		{ type: "free", description: "benefit.pando.additionalDriver" },
		{ type: "free", description: "benefit.pando.claimAssistance" },
		{ type: "", description: "benefit.pando.est" },
		{ type: "", description: "benefit.pando.votedBest" },
		{ type: "", description: "benefit.pando.insuranceAcceptance" }
	],
	AXA: [
		{ type: "driver", description: "benefit.AXA.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.AXA.benefitTowingService" },
		{ type: "free", description: "benefit.AXA.warranty" },
		{ type: "free", description: "benefit.AXA.roadsideAssistance" },
		{ type: "free", description: "benefit.AXA.rideupTo30KM" },
		{ type: "free", description: "benefit.AXA.additionalBenefits" },
		{ type: "", description: "benefit.AXA.instantApproval" },
		{ type: "", description: "benefit.AXA.fastClaimApproval" }
	],
	Sompo: [
		{ type: "driver", description: "benefit.sompo.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.sompo.benefitTowingService" },
		{ type: "free", description: "benefit.sompo.allDriversCoverage" },
		{ type: "free", description: "benefit.sompo.roadsideAssistanceRakanAuto" },
		{ type: "free", description: "benefit.sompo.nilExcess" },
		{ type: "free", description: "benefit.sompo.specialPerlisCoverage" },
		{ type: "", description: "benefit.sompo.ehailing" }
	],
	Takaful_Malaysia: [
		{
			type: "driver",
			description: "benefit.TakafulMalaysia.additionalProtectedDriver"
		},
		{
			type: "24hrTowing",
			description: "benefit.TakafulMalaysia.benefitTowingService"
		},
		{
			type: "free",
			description: "benefit.TakafulMalaysia.changeBatteryService"
		},
		{ type: "free", description: "benefit.TakafulMalaysia.refuelTank" },
		{ type: "free", description: "benefit.TakafulMalaysia.tireReplacement" },
		{
			type: "free",
			description: "benefit.TakafulMalaysia.roadsideAssistanceProgram"
		},
		{ type: "free", description: "benefit.TakafulMalaysia.claimAssistance" },
		{ type: "", description: "benefit.TakafulMalaysia.instantApproval" },
		{ type: "", description: "benefit.TakafulMalaysia.fastClaimApproval" },
		{ type: "", description: "benefit.TakafulMalaysia.fireInsurer" },
		{ type: "", description: "benefit.TakafulMalaysia.theftInsurer" },
		{ type: "", description: "benefit.TakafulMalaysia.insuranceOldVehicles" },
		{ type: "", description: "benefit.TakafulMalaysia.cashBack" },
		{ type: "", description: "benefit.TakafulMalaysia.suitableNonMuslims" }
	],
	Liberty: [
		{
			type: "driver",
			description: "benefit.Liberty.additionalProtectedDriver"
		},
		{ type: "towing", description: "benefit.Liberty.benefitTowingService" },
		{ type: "free", description: "benefit.Liberty.emergencyTowing" },
		{ type: "free", description: "benefit.Liberty.roadsideAssistance" },
		{ type: "free", description: "benefit.Liberty.changeBatteryService" },
		{ type: "free", description: "benefit.Liberty.refuelEmptyTank" },
		{ type: "free", description: "benefit.Liberty.replaceFlatTyre" },
		{ type: "free", description: "benefit.Liberty.additionalDriver" },
		{ type: "free", description: "benefit.Liberty.claimAssistance" },
		{ type: "free", description: "benefit.Liberty.paCoverage" },
		{ type: "free", description: "benefit.Liberty.medicalExpenses" },
		{ type: "free", description: "benefit.Liberty.keyReplacement" },
		{ type: "free", description: "benefit.Liberty.childseatReplacement" },
		{ type: "", description: "benefit.Liberty.ehailing" }
	],
	Ikhlas: [
		{
			type: "driver",
			description: "benefit.TakafulIkhlas.additionalProtectedDriver"
		},
		{
			type: "towing",
			description: "benefit.TakafulIkhlas.benefitTowingService"
		},
		{ type: "free", description: "benefit.TakafulIkhlas.emergencyTowing" },
		{
			type: "free",
			description: "benefit.TakafulIkhlas.roadsideAssistanceBantuanIkhlas"
		},
		{ type: "free", description: "benefit.TakafulIkhlas.changeBatteryService" },
		{ type: "free", description: "benefit.TakafulIkhlas.changingFlatTyre" },
		{ type: "free", description: "benefit.TakafulIkhlas.petrolAssist" },
		{ type: "free", description: "benefit.TakafulIkhlas.lockSmitch" },
		{ type: "free", description: "benefit.TakafulIkhlas.personalAccInsurance" }
	],
	Allianz: [
		{
			type: "driver",
			description: "benefit.Allianz.additionalProtectedDriver"
		},
		{ type: "24hrTowing", description: "benefit.Allianz.benefitTowingService" },
		{ type: "free", description: "benefit.Allianz.claimAssistance" },
		{ type: "", description: "benefit.Allianz.ehailing" },
		{ type: "", description: "benefit.Allianz.instantApproval" },
		{ type: "", description: "benefit.Allianz.fireInsurer" },
		{ type: "", description: "benefit.Allianz.theftInsurer" },
		{ type: "", description: "benefit.Allianz.bestCarInsurer" }
	],
	Etiqa: [
		{ type: "driver", description: "benefit.Etiqa.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.Etiqa.benefitTowingService" },
		{ type: "free", description: "benefit.Etiqa.allDriversCoverage" },
		{ type: "free", description: "benefit.Etiqa.roadsideAssistance" },
		{ type: "", description: "benefit.Etiqa.bestMotorTakaful" }
	],
	Tune: [
		{
			type: "driver",
			description: "benefit.Tune_Protect.additionalProtectedDriver"
		},
		{
			type: "towing",
			description: "benefit.Tune_Protect.benefitTowingService"
		},
		{ type: "", description: "benefit.Tune_Protect.fireInsurer" },
		{ type: "", description: "benefit.Tune_Protect.theftInsurer" }
	],
	Zurich: [
		{ type: "driver", description: "benefit.Zurich.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.Zurich.benefitTowingService" },
		{ type: "free", description: "benefit.Zurich.emergencyTowing" },
		{ type: "free", description: "benefit.Zurich.claimAssistance" },
		{ type: "", description: "benefit.Zurich.fastClaimApproval" },
		{ type: "", description: "benefit.Zurich.instantApproval" },
		{ type: "", description: "benefit.Zurich.ehailing" }
	],
	Zurich_Takaful: [
		{
			type: "24hrTowing",
			description: "benefit.Zurich_Takaful.emergencyTowing"
		},
		{ type: "free", description: "benefit.Zurich_Takaful.roadsideAssistance" },
		{ type: "free", description: "benefit.Zurich_Takaful.roadsideRepair" },
		{ type: "free", description: "benefit.Zurich_Takaful.claimAssistance" },
		{ type: "free", description: "benefit.Zurich_Takaful.windscreenCoverage" },
		{ type: "", description: "benefit.Zurich_Takaful.eHailing" },
		{ type: "", description: "benefit.Zurich_Takaful.instantApproval" },
		{ type: "", description: "benefit.Zurich_Takaful.easyAndFastClaims" }
	],
	RHB: [
		{ type: "driver", description: "benefit.RHB.additionalProtectedDriver" },
		{ type: "24hrTowing", description: "benefit.RHB.benefitTowingService" },
		{ type: "free", description: "benefit.RHB.changeBatteryService" },
		{ type: "free", description: "benefit.RHB.refuelEmptyTank" },
		{ type: "free", description: "benefit.RHB.tireReplacement" },
		{ type: "free", description: "benefit.RHB.roadsideAssistance" },
		{ type: "free", description: "benefit.RHB.claimAssistance" },
		{ type: "", description: "benefit.RHB.fireInsurer" },
		{ type: "", description: "benefit.RHB.theftInsurer" }
	],
	Kurnia: [
		{ type: "driver", description: "benefit.Kurnia.additionalProtectedDriver" },
		{ type: "towing", description: "benefit.Kurnia.benefitTowingService" },
		{ type: "free", description: "benefit.Kurnia.autoAssistKurnia" },
		{ type: "free", description: "benefit.Kurnia.emergencyTowing" },
		{ type: "free", description: "benefit.Kurnia.additionalDriver" },
		{ type: "", description: "benefit.Kurnia.fireInsurer" },
		{ type: "", description: "benefit.Kurnia.theftInsurer" }
	],
	Pacific: [
		{
			type: "driver",
			description: "benefit.Pacific.additionalProtectedDriver"
		},
		{ type: "towing", description: "benefit.Pacific.benefitTowingService" },
		{ type: "free", description: "benefit.Pacific.roadsideAssistance" },
		{ type: "free", description: "benefit.Pacific.additionalDriver" },
		{ type: "", description: "benefit.Pacific.fireInsurer" },
		{ type: "", description: "benefit.Pacific.theftInsurer" },
		{ type: "", description: "benefit.Pacific.fastClaimApproval" }
	],
	Chubb: [
		{ type: "included", description: "benefit.Chubb.accidentalDeath" },
		{ type: "included", description: "benefit.Chubb.permanentDisablement" },
		{ type: "included", description: "benefit.Chubb.funeralExpenses" },
		{ type: "included", description: "benefit.Chubb.accidentalHospitalIncome" },
		{ type: "included", description: "benefit.Chubb.doubleIndemnity" },
		//{ type: "included", description: "benefit.Chubb.towingAndMinorRepairService" },
		{ type: "included", description: "benefit.Chubb.naturalDisasterRelief" },
		//{ type: "", description: "benefit.Chubb.eHailing" },
		{ type: "", description: "benefit.Chubb.instantApproval" },
		{ type: "", description: "benefit.Chubb.easyAndFastClaims" }
	]
};

export const INSURER_BENEFITS_LIST_TPFT = {
	GrabAXA: [
		{
			type: "driver",
			description: "benefit.AXAGrab.additionalProtectedDriver"
		},
		{ type: "", description: "benefit.AXAGrab.safeDrivingDiscountWithDesc" },
		{ type: "", description: "benefit.AXAGrab.instantApproval" },
		{ type: "", description: "benefit.AXAGrab.fastClaimApproval" },
		{ type: "", description: "benefit.AXAGrab.personalAccidentCarOwner" },
		{ type: "", description: "benefit.AXAGrab.medicalClaims" },
		{ type: "", description: "benefit.AXAGrab.lossOfIncome" },
		{ type: "", description: "benefit.AXAGrab.loanProtection" },
		{ type: "", description: "benefit.AXAGrab.wheelAlignment" }
	],
	MSIG: [
		{ type: "driver", description: "benefit.MSIG.additionalProtectedDriver" },
		{ type: "free", description: "benefit.MSIG.roadsideAssistance" },
		{ type: "free", description: "benefit.MSIG.claimAssistance" },
		{ type: "", description: "benefit.MSIG.ehailing" }
	],
	Pando: [
		{ type: "driver", description: "benefit.pando.additionalProtectedDriver" },
		{ type: "free", description: "benefit.pando.towingService" },
		{ type: "free", description: "benefit.pando.roadsideAssistance" },
		{ type: "free", description: "benefit.pando.changeBatteryService" },
		{ type: "free", description: "benefit.pando.refuelEmptyTank" },
		{ type: "free", description: "benefit.pando.tireReplacement" },
		{ type: "free", description: "benefit.pando.additionalDriver" },
		{ type: "free", description: "benefit.pando.claimAssistance" },
		{ type: "", description: "benefit.pando.est" },
		{ type: "", description: "benefit.pando.votedBest" },
		{ type: "", description: "benefit.pando.insuranceAcceptance" }
	],
	AXA: [
		{ type: "driver", description: "benefit.AXA.additionalProtectedDriver" },
		{ type: "free", description: "benefit.AXA.warranty" },
		{ type: "free", description: "benefit.AXA.roadsideAssistance" },
		{ type: "free", description: "benefit.AXA.rideupTo30KM" },
		{ type: "free", description: "benefit.AXA.additionalBenefits" },
		{ type: "", description: "benefit.AXA.instantApproval" },
		{ type: "", description: "benefit.AXA.fastClaimApproval" }
	],
	Sompo: [
		{ type: "driver", description: "benefit.sompo.additionalProtectedDriver" },
		{ type: "free", description: "benefit.sompo.roadsideAssistanceRakanAuto" },
		{ type: "free", description: "benefit.sompo.nilExcess" },
		{ type: "free", description: "benefit.sompo.specialPerlisCoverage" },
		{ type: "", description: "benefit.sompo.ehailing" }
	],
	Takaful_Malaysia: [
		{
			type: "driver",
			description: "benefit.TakafulMalaysia.additionalProtectedDriver"
		},
		{
			type: "24hrTowing",
			description: "benefit.TakafulMalaysia.benefitTowingService"
		},
		{
			type: "free",
			description: "benefit.TakafulMalaysia.changeBatteryService"
		},
		{ type: "free", description: "benefit.TakafulMalaysia.refuelTank" },
		{ type: "free", description: "benefit.TakafulMalaysia.tireReplacement" },
		{
			type: "free",
			description: "benefit.TakafulMalaysia.roadsideAssistanceProgram"
		},
		{ type: "free", description: "benefit.TakafulMalaysia.claimAssistance" },
		{ type: "", description: "benefit.TakafulMalaysia.instantApproval" },
		{ type: "", description: "benefit.TakafulMalaysia.fastClaimApproval" },
		{ type: "", description: "benefit.TakafulMalaysia.fireInsurer" },
		{ type: "", description: "benefit.TakafulMalaysia.theftInsurer" },
		{ type: "", description: "benefit.TakafulMalaysia.insuranceOldVehicles" },
		{ type: "", description: "benefit.TakafulMalaysia.cashBack" },
		{ type: "", description: "benefit.TakafulMalaysia.suitableNonMuslims" }
	],
	Liberty: [
		{
			type: "driver",
			description: "benefit.Liberty.additionalProtectedDriver"
		},
		{ type: "free", description: "benefit.Liberty.emergencyTowing" },
		{ type: "free", description: "benefit.Liberty.roadsideAssistance" },
		{ type: "free", description: "benefit.Liberty.changeBatteryService" },
		{ type: "free", description: "benefit.Liberty.refuelEmptyTank" },
		{ type: "free", description: "benefit.Liberty.replaceFlatTyre" },
		{ type: "free", description: "benefit.Liberty.additionalDriver" },
		{ type: "free", description: "benefit.Liberty.claimAssistance" },
		{ type: "free", description: "benefit.Liberty.paCoverage" },
		{ type: "free", description: "benefit.Liberty.medicalExpenses" },
		{ type: "free", description: "benefit.Liberty.keyReplacement" },
		{ type: "free", description: "benefit.Liberty.childseatReplacement" },
		{ type: "", description: "benefit.Liberty.ehailing" }
	],
	Ikhlas: [
		{
			type: "driver",
			description: "benefit.TakafulIkhlas.additionalProtectedDriver"
		},
		{
			type: "free",
			description: "benefit.TakafulIkhlas.roadsideAssistanceBantuanIkhlas"
		},
		{ type: "free", description: "benefit.TakafulIkhlas.changeBatteryService" },
		{ type: "free", description: "benefit.TakafulIkhlas.changingFlatTyre" },
		{ type: "free", description: "benefit.TakafulIkhlas.petrolAssist" },
		{ type: "free", description: "benefit.TakafulIkhlas.lockSmitch" },
		{ type: "free", description: "benefit.TakafulIkhlas.personalAccInsurance" }
	],
	Allianz: [
		{
			type: "driver",
			description: "benefit.Allianz.additionalProtectedDriver"
		},
		{ type: "24hrTowing", description: "benefit.Allianz.benefitTowingService" },
		{ type: "free", description: "benefit.Allianz.claimAssistance" },
		{ type: "", description: "benefit.Allianz.ehailing" },
		{ type: "", description: "benefit.Allianz.instantApproval" },
		{ type: "", description: "benefit.Allianz.fireInsurer" },
		{ type: "", description: "benefit.Allianz.theftInsurer" },
		{ type: "", description: "benefit.Allianz.bestCarInsurer" }
	],
	Etiqa: [
		{ type: "driver", description: "benefit.Etiqa.additionalProtectedDriver" },
		{ type: "free", description: "benefit.Etiqa.roadsideAssistance" },
		{ type: "", description: "benefit.Etiqa.bestMotorTakaful" }
	],
	Tune: [
		{
			type: "driver",
			description: "benefit.Tune_Protect.additionalProtectedDriver"
		},
		{ type: "", description: "benefit.Tune_Protect.fireInsurer" },
		{ type: "", description: "benefit.Tune_Protect.theftInsurer" }
	],
	Zurich: [
		{ type: "driver", description: "benefit.Zurich.additionalProtectedDriver" },
		{ type: "free", description: "benefit.Zurich.claimAssistance" },
		{ type: "", description: "benefit.Zurich.fastClaimApproval" },
		{ type: "", description: "benefit.Zurich.instantApproval" },
		{ type: "", description: "benefit.Zurich.ehailing" }
	],
	Zurich_Takaful: [
		{
			type: "free",
			description: "benefit.Zurich_Takaful.roadsideAssistance"
		},
		{
			type: "free",
			description: "benefit.Zurich_Takaful.claimAssistance"
		},
		{ type: "", description: "benefit.Zurich_Takaful.coverForFEL" },
		{ type: "", description: "benefit.Zurich_Takaful.coverForTheft" },
		{
			type: "",
			description: "benefit.Zurich_Takaful.lossOrDamageProperty"
		},
		{ type: "", description: "benefit.Zurich_Takaful.bodilyInjury" }
	],
	RHB: [
		{ type: "driver", description: "benefit.RHB.additionalProtectedDriver" },
		{ type: "24hrTowing", description: "benefit.RHB.benefitTowingService" },
		{ type: "free", description: "benefit.RHB.changeBatteryService" },
		{ type: "free", description: "benefit.RHB.refuelEmptyTank" },
		{ type: "free", description: "benefit.RHB.tireReplacement" },
		{ type: "free", description: "benefit.RHB.roadsideAssistance" },
		{ type: "free", description: "benefit.RHB.claimAssistance" },
		{ type: "", description: "benefit.RHB.fireInsurer" },
		{ type: "", description: "benefit.RHB.theftInsurer" }
	],
	Kurnia: [
		{ type: "driver", description: "benefit.Kurnia.additionalProtectedDriver" },
		{ type: "", description: "benefit.Kurnia.fireInsurer" },
		{ type: "", description: "benefit.Kurnia.theftInsurer" }
	],
	Pacific: [
		{
			type: "driver",
			description: "benefit.Pacific.additionalProtectedDriver"
		},
		{ type: "", description: "benefit.Pacific.fireInsurer" },
		{ type: "", description: "benefit.Pacific.theftInsurer" },
		{ type: "", description: "benefit.Pacific.fastClaimApproval" }
	],
	Chubb: [
		{ type: "driver", description: "benefit.Chubb.additionalProtectedDriver" },
		{
			type: "unlimitedTowing",
			description: "benefit.Chubb.benefitTowingService"
		},
		{ type: "free", description: "benefit.Chubb.changeBatteryService" },
		{ type: "free", description: "benefit.Chubb.refuelTank" },
		{ type: "free", description: "benefit.Chubb.tireReplacement" },
		{ type: "free", description: "benefit.Chubb.roadsideAssistance" },
		{ type: "free", description: "benefit.Chubb.claimAssistance" },
		{ type: "", description: "benefit.Chubb.instantApproval" },
		{ type: "", description: "benefit.Chubb.fastClaimApproval" },
		{ type: "", description: "benefit.Chubb.minorRepairService" }
	]
};
